import { useEffect, useMemo, useState } from 'react';
import { Box, FormControl, FormHelperText, FormLabel, IconButton, Stack, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import ImageIcon from '@mui/icons-material/Image';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';

type RichTextEditorProps = {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  minHeight?: number;
};

const RichTextEditor = ({
  label,
  value,
  onChange,
  onBlur,
  disabled,
  error,
  helperText,
  minHeight = 200,
}: RichTextEditorProps) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto; margin: 8px 0;',
        },
      }),
    ],
    content: value || '<p></p>',
    editable: !disabled,
    parseOptions: {
      preserveWhitespace: 'full',
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const value = editor.isEmpty ? '' : html;
      onChange(value);
    },
    onBlur: () => {
      if (onBlur) {
        onBlur();
      }
    },
    editorProps: {
      attributes: {
        class: 'rich-text-editor-content',
      },
    },
  });

  const safeValue = useMemo(() => value || '', [value]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const current = editor.isEmpty ? '' : editor.getHTML();
    if (safeValue !== current) {
      editor.commands.setContent(safeValue || '<p></p>', false);
    }
  }, [editor, safeValue]);

  const toggleMark = (mark: 'bold' | 'italic') => {
    if (!editor) {
      return;
    }

    const chain = editor.chain().focus();
    if (mark === 'bold') {
      chain.toggleBold().run();
    } else {
      chain.toggleItalic().run();
    }
  };

  const toggleList = (list: 'bullet' | 'ordered') => {
    if (!editor) {
      return;
    }

    const chain = editor.chain().focus();
    if (list === 'bullet') {
      chain.toggleBulletList().run();
    } else {
      chain.toggleOrderedList().run();
    }
  };

  const handleUndo = () => {
    editor?.chain().focus().undo().run();
  };

  const handleRedo = () => {
    editor?.chain().focus().redo().run();
  };

  const handleInsertImage = async () => {
    if (!editor || !selectedFile) {
      return;
    }

    setUploading(true);
    try {
      // Get token from localStorage - check multiple possible keys
      let token = localStorage.getItem('token');
      if (!token) {
        token = localStorage.getItem('accessToken');
      }
      if (!token) {
        token = localStorage.getItem('auth_token');
      }

      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch('/api/uploads/images', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload failed:', errorText);
        throw new Error(`Failed to upload image: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      const imageUrl = result.data.url;

      // Insert image using TipTap's Image extension
      editor.chain().focus().insertContent(`<img src="${imageUrl}" alt="Uploaded image" />`).run();

      setSelectedFile(null);
      setImageDialogOpen(false);
    } catch (error) {
      console.error('Image upload error:', error);
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleImageDialogClose = () => {
    setImageDialogOpen(false);
    setSelectedFile(null);
  };

  return (
    <FormControl fullWidth error={error} sx={{ gap: 1 }}>
      {label ? <FormLabel>{label}</FormLabel> : null}
      <Box
        sx={{
          border: (theme) => `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: disabled ? 'action.disabledBackground' : 'background.paper',
          transition: 'border-color 0.2s ease-in-out',
        }}
      >
        <Stack
          direction="row"
          spacing={0}
          sx={{
            p: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.default',
          }}
        >
          <Tooltip title="Bold">
            <span>
              <IconButton
                size="small"
                onClick={() => toggleMark('bold')}
                disabled={disabled || !editor?.can().chain().focus().toggleBold().run()}
              >
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Italic">
            <span>
              <IconButton
                size="small"
                onClick={() => toggleMark('italic')}
                disabled={disabled || !editor?.can().chain().focus().toggleItalic().run()}
              >
                <FormatItalicIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Bullet list">
            <span>
              <IconButton
                size="small"
                onClick={() => toggleList('bullet')}
                disabled={disabled || !editor?.can().chain().focus().toggleBulletList().run()}
              >
                <FormatListBulletedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Numbered list">
            <span>
              <IconButton
                size="small"
                onClick={() => toggleList('ordered')}
                disabled={disabled || !editor?.can().chain().focus().toggleOrderedList().run()}
              >
                <FormatListNumberedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Insert image">
            <span>
              <IconButton
                size="small"
                onClick={() => setImageDialogOpen(true)}
                disabled={disabled}
              >
                <ImageIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Undo">
            <span>
              <IconButton size="small" onClick={handleUndo} disabled={disabled || !editor?.can().undo()}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo">
            <span>
              <IconButton size="small" onClick={handleRedo} disabled={disabled || !editor?.can().redo()}
              >
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Box
          sx={{
            p: 2,
            minHeight,
            '& .ProseMirror': {
              outline: 'none',
              minHeight,
              '& p': {
                margin: 0,
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                margin: '8px 0',
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
              },
            },
            '& .ProseMirror p + p': {
              marginTop: 1,
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}

      <Dialog open={imageDialogOpen} onClose={handleImageDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="image-file-input"
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setSelectedFile(file);
              }
            }}
          />
          <label htmlFor="image-file-input">
            <Button
              variant="outlined"
              component="span"
              fullWidth
              sx={{ mt: 2, mb: 2 }}
            >
              Choose Image File
            </Button>
          </label>
          {selectedFile && (
            <Box sx={{ mt: 1, mb: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                value={selectedFile.name}
                InputProps={{
                  readOnly: true,
                }}
                helperText={`Size: ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImageDialogClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleInsertImage}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : null}
          >
            {uploading ? 'Uploading...' : 'Insert Image'}
          </Button>
        </DialogActions>
      </Dialog>
    </FormControl>
  );
};

export default RichTextEditor;
